import { useEffect, useState } from 'react';
import {
  MIC_TYPES, MIC_TYPE_SHORT, PALETTE, minutesToHHMM, liveStateOf, byRelevance,
  type Assignment, type Channel, type MicType, type Presenter,
} from '@micplot/shared';
import Avatar from './Avatar';
import { useNowMinutes } from '../lib/now';
import RfMeter from './RfMeter';
import type { TelemetryReading } from '@micplot/shared';

interface Props {
  channel: Channel;
  assignments: Assignment[];
  presenterById: Map<string, Presenter>;
  canEdit: boolean;
  conflicted: boolean;
  dragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onPatch: (patch: Partial<Channel>) => void;
  onUnassign: (assignmentId: string) => void;
  /** True when a presenter is waiting to be placed, so this card is a target. */
  armed?: boolean;
  /** Tap-to-place, the only route onto a mic that works on a touchscreen. */
  onTapAssign?: () => void;
  /** Live reading for this channel, matched by frequency. */
  telemetry?: TelemetryReading;
  /** Removing a transmitter from the rig. */
  onDelete?: () => void;
}

const nextType = (t: MicType): MicType =>
  MIC_TYPES[(MIC_TYPES.indexOf(t) + 1) % MIC_TYPES.length]!;

/**
 * One RF channel. Doubles as the drop target on the A2 board.
 *
 * When a presenter is assigned the card floods with their colour -- at a dark
 * front-of-house position that block of colour is readable from across the
 * room, which a coloured border alone is not.
 */
export default function ChannelCard({
  channel, assignments, presenterById, canEdit, conflicted, dragOver,
  onDragOver, onDragLeave, onDrop, onPatch, onUnassign, telemetry, armed, onTapAssign,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(channel.label ?? '');
  const [freq, setFreq] = useState(channel.frequency?.toFixed(3) ?? '');

  useEffect(() => {
    setLabel(channel.label ?? '');
    setFreq(channel.frequency?.toFixed(3) ?? '');
  }, [channel.label, channel.frequency]);

  /*
   * The card is painted by whoever is on this mic *now*, not by whichever
   * assignment happens to start earliest. A channel used in the morning and
   * again in the evening was showing the morning presenter's colour all night,
   * which is exactly backwards from what the board is for.
   */
  const now = useNowMinutes();
  const [showHistory, setShowHistory] = useState(false);
  const ordered = byRelevance(assignments, now);
  const done = ordered.filter(a => liveStateOf(a, now) === 'past');
  const showing = showHistory ? ordered : ordered.filter(a => !done.includes(a));
  const primary = ordered[0];
  const primaryPresenter = primary ? presenterById.get(primary.presenterId) : undefined;
  const primaryState = primary ? liveStateOf(primary, now) : undefined;
  // A finished assignment should not flood the card -- the mic is free.
  const fill = primaryPresenter && primaryState !== 'past'
    ? PALETTE[primaryPresenter.color % PALETTE.length]
    : undefined;

  function commit() {
    setEditing(false);
    const parsedFreq = freq.trim() === '' ? undefined : Number(freq);
    onPatch({
      label: label.trim(),
      frequency: Number.isFinite(parsedFreq) ? parsedFreq : undefined,
    });
  }

  return (
    <div
      className={[
        'ch', assignments.length > 0 && 'filled',
        dragOver && 'over', conflicted && 'conflict',
        armed && 'armed',
      ].filter(Boolean).join(' ')}
      style={fill ? { background: fill, borderColor: fill, color: '#0d1113' } : undefined}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/*
        * Tap to place. Dragging is a mouse API and does nothing on a phone, and
        * the number-key route needs a keyboard a phone will not offer, so
        * without this the board cannot be worked from a touchscreen at all.
        *
        * A dedicated layer rather than a handler on the card: the card is full
        * of its own controls -- the name editor covers most of it -- so a tap
        * aimed at the channel usually lands on one of them.
        */}
      {armed && onTapAssign && (
        <button
          className="ch-tap"
          onClick={onTapAssign}
          aria-label={`Place the selected presenter on TX ${channel.number}`}
        >
          <span>Place here</span>
        </button>
      )}
      <div className="ch-head">
        <span className="ch-num">{channel.number}</span>

        <button
          className="type-btn"
          disabled={!canEdit || channel.locked}
          title={channel.locked
            ? 'Transmitter type is locked'
            : 'Click to change transmitter type'}
          onClick={() => onPatch({ micType: nextType(channel.micType) })}
        >
          {MIC_TYPE_SHORT[channel.micType]}
        </button>

        <button
          className="type-btn"
          disabled={!canEdit}
          aria-pressed={channel.locked}
          title={channel.locked
            ? 'Unlock: allow the transmitter type to change'
            : 'Lock the transmitter type to this element'}
          onClick={() => onPatch({ locked: !channel.locked })}
        >
          {channel.locked ? 'LOCKED' : 'UNLOCKED'}
        </button>

        <div className="header-spacer" />
        {telemetry && <RfMeter t={telemetry} compact />}
        {onDelete && (
          <button className="ch-del" onClick={onDelete}
                  title={`Remove TX ${channel.number}`}
                  aria-label={`Remove TX ${channel.number}`}>✕</button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'grid', gap: 5 }}>
          <input
            autoFocus value={label} placeholder="Channel name"
            aria-label="Channel name"
            onChange={e => setLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && commit()}
          />
          <input
            value={freq} placeholder="000.000" inputMode="decimal"
            aria-label="Frequency in MHz"
            onChange={e => setFreq(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && commit()}
          />
          <button className="btn sm" onClick={commit}>Save</button>
        </div>
      ) : (
        <button
          className="ch-meta"
          disabled={!canEdit}
          title={canEdit ? 'Click to edit name and frequency' : undefined}
          onClick={() => setEditing(true)}
        >
          <span className="ch-name">{channel.label || 'CH NAME'}</span>
          <span className="ch-freq">
            {channel.frequency ? channel.frequency.toFixed(3) : '000.000'}
          </span>
        </button>
      )}

      <div className="ch-body">
        {/* Everyone on this channel having finished means the mic is free, and
            saying so is more use than an empty box under a history toggle. */}
        {showing.length === 0 ? (
          <div style={{ display: 'grid', gap: 5, width: '100%' }}>
            <span className="ch-drop">Drop presenter here</span>
            {done.length > 0 && (
              <button className="ch-history" aria-expanded={showHistory}
                      onClick={() => setShowHistory(true)}>
                ▸ {done.length} earlier
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 5, width: '100%' }}>
            {/*
              * Finished assignments fold away.
              *
              * A channel worked hard across a two-act show collects five or six
              * of these, each three lines deep, and the card ends up a wall of
              * greyed-out history with the one person actually on the mic
              * somewhere inside it. The past is worth being able to see -- who
              * had this pack before matters when it comes back with a flat
              * battery -- but not at the cost of the present.
              */}
            {showing.map(a => {
              const p = presenterById.get(a.presenterId);
              if (!p) return null;
              const state = liveStateOf(a, now);
              return (
                <div key={a.id} className={`row a-${state}`} style={{ gap: 8 }}>
                  <Avatar presenter={p} size={34} onFill />
                  {state === 'live' && (
                    <span className="on-air" title="On this mic right now">ON</span>
                  )}
                  <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <div className="ch-assigned">{p.name}</div>
                    {p.characterName && (
                      <div className="ch-character">{p.characterName}</div>
                    )}
                    <div className="ch-when">
                      {/* No times means the mic is held for the whole show; rendering
                          that as a clock range read as "00:00-00:00". */}
                      {a.scheduled
                        ? `${minutesToHHMM(a.start)}–${minutesToHHMM(a.end)}`
                        : 'whole show'}
                      {state === 'past' && ' · done'}
                      {state === 'upcoming' && ' · later'}
                      {!a.deployed && ' · staged'}
                    </div>
                  </span>
                  {canEdit && (
                    <button className="ch-x" aria-label={`Unassign ${p.name}`}
                            onClick={() => onUnassign(a.id)}>✕</button>
                  )}
                </div>
              );
            })}

            {done.length > 0 && (
              <button className="ch-history" aria-expanded={showHistory}
                      onClick={() => setShowHistory(v => !v)}>
                {showHistory
                  ? '▾ hide earlier'
                  : `▸ ${done.length} earlier`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
